// Core imports
import { Fragment, useState, useEffect, useMemo } from 'react';
import { t } from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Button, Popover, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';
import NodeEditForm from '../../NodeEditForm';

// Misc imports
import { truncate } from '../../../../utils/string/truncate';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

const CarouselButton = ({
  selectedNode,
  cardId,
  cardIndex = 0,
  button,
  buttonUrl,
  buttonIndex = 0
}) => {
  const { isEditingPrimaryLanguage } = useBotLanguage();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // If a click happens outside the popover, close it
  useEffect(() => {
    document.addEventListener('click', (e) => {
      if (e.target.id !== `${cardId}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`) {
        setIsPopoverOpen(false);
      }
    });
  }, []);

  const renderHandle = () => {
    return (
      <LimitedConnectionsHandle
        id={`${cardId}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`}
        className={classNames({ 'visibility-hidden': buttonUrl }, { 'opacity-0': !button })}
        type="source"
        position="right"
        allowedOutgoingEdgesCount={3}
      />
    );
  };

  return (
    <Popover
      key={buttonIndex}
      overlayStyle={{ width: 300 }}
      open={isPopoverOpen}
      onOpenChange={(open) => setIsPopoverOpen(open)}
      trigger="click"
      placement="right"
      destroyTooltipOnHide
      content={
        <NodeEditForm
          selectedElement={selectedNode}
          metadata={{
            typeOfForm: 'popover',
            cardIndex: cardIndex,
            buttonIndex: buttonIndex,
            sourceHandle: `${cardId}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`
          }}
          onAfterSubmit={() => setIsPopoverOpen(false)}
          onAfterCancel={() => setIsPopoverOpen(false)}
        />
      }
    >
      <Fragment>
        {
          button ? (
            <span
              id={`${cardId}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`}
              className="cardWrapper border-color-muted text-left nodrag bg-layout"
            >
              <Space.Compact size="small" style={{ minHeight: 45 }} className="pl-2 justify-content-center w-100" direction="vertical">
                <Typography
                  className={classNames('py-0 w-100', { 'font-style-italic': !button?.title })}
                >
                  {truncate(button?.title || t('Add button label'), 20, '...')}
                </Typography>

                {
                  buttonUrl ? (
                    <Typography type="secondary" className="fs-xs py-0 w-100" italic>
                      {truncate(buttonUrl, 30, '...')}
                    </Typography>
                  ) : null
                }
              </Space.Compact>

              {renderHandle()}
            </span>
          ) : (
            <Button
              id={`${cardId}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`}
              type="dashed"
              icon={<PlusOutlined />}
              block
              size="large"
              className="text-left nodrag py-3 h-auto"
              disabled={!isEditingPrimaryLanguage}
            >
              {t('Add Button')}
              {renderHandle()}
            </Button>
          )
        }
      </Fragment>
    </Popover>
  );
};

/** A component that renders the buttons for a carousel card
 * @param {object} props - The component props
 * @param {object} props.card - The card object
 * @param {number} props.cardIndex - The index of the card where the button is located
 */
const CarouselCardButtons = ({
  selectedNode,
  card,
  cardIndex
}) => {
  const { currentBotLanguage, primaryBotLanguage } = useBotLanguage();

  const buttons = useMemo(() => {
    return card?.[primaryBotLanguage]?.buttons || [];
  }, [card, primaryBotLanguage]);

  const cardId = card?.[primaryBotLanguage]?.id;

  return (
    <Space direction="vertical" size={12} className="w-100 p-3">
      {
        buttons.map((button, buttonIndex) => (
          <CarouselButton
            selectedNode={selectedNode}
            cardId={cardId}
            cardIndex={cardIndex}
            button={card?.[currentBotLanguage]?.buttons[buttonIndex] || {}}
            buttonUrl={card?.[currentBotLanguage]?.buttons[buttonIndex]?.data || card?.[primaryBotLanguage]?.buttons[buttonIndex]?.data}
            buttonIndex={buttonIndex}
          />
        ))
      }

      {
        // "Add new button" option
        buttons.length < 3 ? (
          <CarouselButton
            selectedNode={selectedNode}
            cardId={cardId}
            cardIndex={cardIndex}
            // If the button has no index we are adding a new button
            // New button should be added at the end of the array
            button={null}
            buttonIndex={buttons.length}
          />
        ) : null
      }
    </Space>
  );
};

CarouselCardButtons.propTypes = {
  selectedNode: PropTypes.object,
  card: PropTypes.object,
  cardIndex: PropTypes.number
};

CarouselButton.propTypes = {
  selectedNode: PropTypes.object,
  cardId: PropTypes.string,
  cardIndex: PropTypes.number,
  button: PropTypes.object,
  buttonUrl: PropTypes.string,
  buttonIndex: PropTypes.number
};

export default CarouselCardButtons;
